import type { FunctionComponent } from "react";
import Calendar, { type CalendarTileProperties, type ViewCallback } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "@App/Components/DayPicker/DayPicker.css";
import { CircleDashed } from "@App/Components/atoms/icons/CircleDashed";
import { CircleFull } from "@App/Components/atoms/icons/CircleFull";
import { ChevronLeftIcon } from "@App/Components/common/Icons";
import { DayPickerLoadingOverlay } from "@App/Components/DayPicker/DayPickerLoadingOverlay";
import type { Config } from "@App/configs/interfaces";
import { getConfig } from "@App/utils/getConfig";
import { addMonths, endOfMonth, isSameDay, startOfMonth } from "date-fns";
import { useSelector } from "react-redux";
import { CircleHalfStroke } from "@App/Components/atoms/icons/CircleHalfStroke";
import { selectFleetVehicleReturn } from "@App/state/selectors/fleet";

type DayPickerProps = {
  config?: Config;
  onChange: (event: Date) => void;
  selectedDay: Date | null;
  loading: boolean;
  activeStartDate: Date;
  earliestStartDate: Date;
  days: Date[];
  onChangeMonth: ViewCallback;
  locale?: string;
  maxMonthsAhead?: number;
};

export const DayPicker: FunctionComponent<DayPickerProps> = ({
  config = getConfig(),
  onChange,
  onChangeMonth,
  activeStartDate,
  earliestStartDate,
  loading,
  days,
  selectedDay,
  locale = "de-DE",
  maxMonthsAhead = 3,
}: DayPickerProps) => {
  /**
   * Calculate the minimum and maximum selectable dates for the calendar.
   */
  const minDate = startOfMonth(earliestStartDate);
  const maxDate = addMonths(endOfMonth(earliestStartDate), maxMonthsAhead);

  const dayIsAvailable = (day: Date): boolean => {
    return days.some((date) => isSameDay(date, day));
  };

  const fleetVehicleReturn = useSelector(selectFleetVehicleReturn);

  const tileContent = ({ date }: CalendarTileProperties) => {
    if (!dayIsAvailable(date)) return <CircleDashed color="gray" className="block mx-auto mt-1" />;

    if (
      fleetVehicleReturn?.endDate &&
      config.dayPicker?.isExtraCostDay(date, fleetVehicleReturn.endDate)
    ) {
      return <CircleHalfStroke color="yellow" className="block mx-auto mt-1" />;
    }

    return <CircleFull color="green" className="block mx-auto mt-1" />;
  };

  /**
   * Disable tiles for days that are not available.
   * @param date - The date of the tile.
   * @returns boolean - True if the tile should be disabled, false otherwise.
   */
  const tileDisabled = ({ date }: CalendarTileProperties): boolean => {
    return !dayIsAvailable(date);
  };

  return (
    <div className={"relative"}>
      <Calendar
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        value={selectedDay ? new Date(selectedDay) : new Date()}
        className={"border border-neutral-500 font-serif p-3 h-full"}
        tileDisabled={tileDisabled}
        minDetail="month"
        locale={locale}
        onActiveStartDateChange={onChangeMonth}
        activeStartDate={activeStartDate}
        tileContent={tileContent}
        prevLabel={<ChevronLeftIcon className={"w-3 h-w"} color={"neutral-100"} />}
        nextLabel={<ChevronLeftIcon className={"w-3 h-w rotate-180"} color={"neutral-100"} />}
      />
      {loading && <DayPickerLoadingOverlay />}
    </div>
  );
};
